import { useState } from 'react';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { bulletinsAPI, errMsg } from '../../lib/regulatoryAPI';

const blank = { source: 'ATO', title: '', published_date: '', url: '', topics: [], impact: 'Medium', summary: '', ingest_method: 'manual' };

export const BulletinDialog = ({ meta, initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...blank, ...initial, published_date: initial.published_date || '', url: initial.url || '' });
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleTopic = (t) => setF({ ...f, topics: f.topics.includes(t) ? f.topics.filter((x) => x !== t) : [...f.topics, t] });
  const preview = async () => {
    setBusy(true);
    try {
      const m = await bulletinsAPI.preview(link);
      if (m.duplicate) toast.warning('A bulletin with this URL already exists');
      setF({ ...f, title: m.title || f.title, summary: m.summary || f.summary, url: m.url, source: m.source, published_date: m.published_date || f.published_date, ingest_method: 'link' });
      toast.success('Prefilled from link – please confirm the details');
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const save = async () => {
    const body = { source: f.source, title: f.title, published_date: f.published_date || null, topics: f.topics, impact: f.impact, summary: f.summary };
    try {
      if (isEdit) await bulletinsAPI.update(initial.id, body);
      else await bulletinsAPI.create({ ...body, url: f.url || null, ingest_method: f.ingest_method, key_dates: [] });
      toast.success(isEdit ? 'Bulletin updated' : 'Bulletin added');
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulletin-dialog">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit bulletin' : 'Add bulletin'}</DialogTitle></DialogHeader>
        {!isEdit && <div className="flex gap-2 rounded-md border bg-muted/40 p-3">
          <Input placeholder="Paste a link (https://…)" value={link} onChange={(e) => setLink(e.target.value)} data-testid="bulletin-link-input" />
          <Button variant="outline" onClick={preview} disabled={!link || busy} data-testid="bulletin-link-fetch">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}Prefill</Button>
        </div>}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Title</Label><Input value={f.title} onChange={set('title')} data-testid="bulletin-title-input" /></div>
          <div><Label>Source</Label><select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={f.source} onChange={set('source')} data-testid="bulletin-source-select">{meta.sources.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><Label>Impact</Label><select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={f.impact} onChange={set('impact')} data-testid="bulletin-impact-select">{meta.impacts.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div><Label>Published</Label><Input type="date" value={f.published_date} onChange={set('published_date')} data-testid="bulletin-date-input" /></div>
          <div><Label>URL</Label><Input value={f.url} onChange={set('url')} disabled={isEdit} placeholder="https://" data-testid="bulletin-url-input" /></div>
          <div className="sm:col-span-2"><Label>Topics</Label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="bulletin-topics">{meta.topics.map((t) => (
              <button key={t} type="button" onClick={() => toggleTopic(t)} data-testid={`bulletin-topic-${t}`}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${f.topics.includes(t) ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>))}</div></div>
          <div className="sm:col-span-2"><Label>Summary (staff-written)</Label><Textarea rows={4} value={f.summary} onChange={set('summary')} data-testid="bulletin-summary-input" /></div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.title} data-testid="bulletin-save-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
